import React from 'react'
import * as yup from 'yup'
import moment from 'moment'
import merge from 'lodash/merge'
import { Link } from 'react-router-dom'

import createSingularContainer from '@components/Singular/createSingularContainer'
import { FormGroup, filterPresets } from '@modules/form/form.module'
import { getTableColumns } from '@scenes/Candidates/CandidateListContainer'
import LocalDataTable from '@components/LocalDataTable/LocalDataTable'
import PrivilegedContent from '@components/PrivilegedContent/PrivilegedContent'

import candidateStatuses from '@server/constants/statuses/candidate-statuses'

import { 
	validatorPresets, 
} from '@modules/form/form.module'

import { ClientModel } from '@modules/user/user.module'

// TODO: make sorting work on local data tables and remove this 
const tableColumns = getTableColumns().map(col => {
	col.sortKey && delete col.sortKey;
	return col;
});

ClientModel.startFieldBuffer();

const formConfig = [
	{
		label: "Client Details",
		fields: ClientModel.getFieldProps([
			'clientName',
			'status',
			'clientID',
			['consultant', {
				label: 'Assigned Consultant',
			}],
			'bio',
			'address',
		]),
	},
	{
		label: "Contact Details",
		fields: ClientModel.getFieldProps([
			'contacts',
		]),
	},
	{
		label: "Financial Details",
		fields: ClientModel.getFieldProps([
			'factored',
			'AWRApplicable',
			'holidayPayRate',
		]),
	},
	{
		label: "Job Types",
		fields: ClientModel.getFieldProps([
			'jobTypeRates',
		]),
	},
	{
		label: "Working Requirements",
		fields: ClientModel.getFieldProps([
			['workingRequirements', {
				label: "",
				placeholder: 'Please detail'
			}]
		])
	},
	{
		label: "Documents",
		fields: ClientModel.getFieldProps([
			['documents', {
				label: ''
			}],
		]),
	},
	{
		label: "Account",
		fields: ClientModel.getFieldProps([
			['email', {
				label: 'Primary Email Address (for login)'
			}],
			['password', {
				show: ({isNew}) => isNew,
			}],
			'passwordConfirm',
		]),
	}
];

const fieldPaths = ClientModel.endFieldBuffer();
// TODO: replace _cachedSchemas with memoized getValidationSchema func!
const _cachedSchemas = {};

export default createSingularContainer({
	Model: ClientModel,
	title: (data) => {
		let title = '';
		if(data) {
			let clientID = data.clientID || '';
			let label = data.label ? data.label : '';
			title = `${label} ${clientID}`;
		}
		return title;
	},
	resource: ClientModel.resourceKey,
	supportsNotes: true,
	supportsDeleting: true,
	mapPropsToValues: (props) => {

		// ensure AWR rates != null
		var data = props.data;
		
		// merge defaults into data
		data = merge({}, ClientModel.getDefaults(), data);

		if(data.jobTypeRates) {
			data.jobTypeRates = data.jobTypeRates.map(line => {
				['standard', 'AWR'].forEach((p) => {
					['payRate', 'chargeRate'].forEach((q) => {
						line[p][q] = line[p][q] == null ? '' : line[p][q];
					});
				});
				return line;
			})
		}

		return data;
	},
	formConfig,
	renderAfterForm: ({data, isNew}) => (
		<React.Fragment>
			{
				!isNew && data && data.candidates && (
					<div>
						<h2>Candidates working for {data.clientName}</h2>
						<LocalDataTable 
							columns={tableColumns}
							filters={filterPresets([
								['status', {
									options: [
										{ label: 'All', value: 'any' },
										...candidateStatuses.map(s => ({
											label: s.label,
											value: s.order,
										})),
									],
									filterHandler: (data, status) => data.filter(item => {
										return item.status.order === status;
									})
								}]
							])}
							rowLinks= {(doc) => (
								<div>
									<Link className="TableRow-link" to={`/candidate/${doc.id}`}>view</Link>
									<PrivilegedContent permissions={['readAny:note']}>
										<Link className="TableRow-link" to={`/candidate/${doc.id}/notes`}>notes</Link>
									</PrivilegedContent>
								</div>
							)}
							data={data.candidates} 
						/>
					</div>
				)
			}
		</React.Fragment>
	),
	validationSchema: ({ isNew }) => {
		// TODO: add auto caching to validation schema based on repeat args?
		const newState = isNew.toString();
		if(!_cachedSchemas[newState]) {
			_cachedSchemas[newState] = ClientModel.getValidationSchema(fieldPaths, { isNew, strictMode: true });
		}
		console.log(_cachedSchemas[newState].describe());
		return _cachedSchemas[newState];
	},
	createdString: 'Joined',
	displayAsAccordion: true,
	mapDataToCSV: (data) => {

		// TODO: add babel optional object read syntax to avoid the need for these consts
		const consultant = data.consultant || {};
		const address = data.address || {};
		const status = data.status || {};
		const contacts = data.contacts || [];
		const candidates = data.candidates || [];
		const jobTypeRates = data.jobTypeRates || [];

		const contactRows = contacts.reduce((arr, contact, idx) => {
			const num = idx+1;
			return [
				...arr, 
				[`Client Contact ${num} Name`, contact.name],
				[`Client Contact ${num} Role`, contact.role],
				[`Client Contact ${num} Telephone`, contact.telephone],
				[`Client Contact ${num} Email`, contact.email],
			];
		}, [])
		
		const jobTypeRows = jobTypeRates.reduce((arr, jobTypeRate, idx) => {
			const num = idx+1;
			const makeRateRows = (key, prefix='') => ([
				[`${prefix}Pay Rate ${num} (£)`, jobTypeRate[key].payRate ],
				[`${prefix}Charge Rate ${num} (£)`, jobTypeRate[key].chargeRate ],
			]);

			const standardRates = makeRateRows('standard');
			const AWRRates = data.AWRApplicable ? makeRateRows('AWR', 'AWR ') : [];

			return [
				...arr,
				[`Job Type ${num}`, jobTypeRate.jobType.label ],
				...standardRates,
				...AWRRates,
			]
		}, [])

		const candidateRows = candidates.reduce((arr, candidate, idx) => {
			const title = idx ? '' : `Candidates working for ${data.clientName}`;
			return [...arr, [title, candidate.label]];
		}, [])


		var output = [
		  ['Client Name', data.clientName],
			['Status', status.label],
			['Assigned Consultant', consultant.label],
			['Client ID', data.clientID],
			...contactRows,
			['Factored / Non Factored', data.factored ],
			['Address Line 1', address.line1],
			['Address Line 2', address.line2],
			['Town / City', address.townCity],
			['County', address.county],
			['Post Code', address.postCode],
			['Bio', data.bio],
			['AWR Applicable', data.AWRApplicable ? 'Yes' : 'No'],
			...jobTypeRows,
			...candidateRows,
			['Added on', moment(data.createdAt).format('DD/MM/YYYY')],
		];

		
		// TODO: add jobTypes
		// TODO: add "candidates working for client"

		// Add job rates
		data.jobTypeRates && data.jobTypeRates.forEach(o => {
			output = output.concat([
				['Job Type', o.jobType.label],
				['Pay Rate', `£${o.standard.payRate}`],
				['Charge Rate', `£${o.standard.payRate}`],
			])

			if(data.AWRApplicable) {
				output = output.concat([
					['AWR Pay Rate', `£${o.AWR.payRate}`],
					['AWR Charge Rate', `£${o.AWR.payRate}`],
				]);
			}
		});

		return output;
	},
	mapDataToCSVFilename: (data) => data.clientName && `h1export-client-${data.clientName.replace(/\s+/g, '-').toLowerCase()}-${moment().format('YYYYMMDD')}.csv`,
});




// WEBPACK FOOTER //
// ./src/scenes/Clients/ClientContainer.jsx